<script setup lang="ts">
import LoyaltyHubRouletteVipCard from './RouletteVipCard.vue';
import type { LoyaltyHubCard } from '~/types/loyalty-hub';
import { loyaltyHubCardArt } from '@/utils/loyalty-hub';
import { vipTierStyle } from '@/utils/vip-tiers';

// Cards de cima entram `eager` (são o que aparece na primeira tela do painel);
// o resto é `lazy` e só é buscado quando a lista rola até ele. Com a caixa da
// arte já reservada (ver `aspect-ratio` no estilo), adiar não mexe no layout.
const EAGER_CARDS = 2;

const props = defineProps<{
  card: LoyaltyHubCard;
  // Índice/total no contrato — definem o span no grid do desktop (os 3 últimos
  // formam a linha de 3 colunas).
  index?: number;
  total?: number;
}>();

const emit = defineEmits<{ (e: 'select', card: LoyaltyHubCard): void }>();

// `enabled: false` no contrato = card desativado: segue na posição da
// `priority` dele, mas com véu leve, ribbon de cadeado e clique inerte.
// Ausente = ativo.
const isLocked = computed(() => props.card.enabled === false);

const lockedLabel = computed(() => props.card.lockedLabel || 'Em breve');

// Tratamento por tier de VIP: o card marcado com `vipTiers` no contrato troca a
// arte estática por um card COMPOSTO (RouletteVipCard) quando o usuário tem tag
// numerada — `VIP_3` → gradiente verde + badge V3 + "Roleta Diária VIP 3".
// Quem é VIP sem número (`VIP_SIGNATURE`), quem não é VIP e os tiers ainda sem
// arte caem no card de imagem de sempre.
// `vipTier` chega `null` no primeiro frame e só resolve quando o perfil da
// Smartico aterrissa — o card padrão é sempre o que aparece primeiro.
const { vipTier } = useVipStatus();

// Vale nos dois breakpoints: o RouletteVipCard tem o desenho mobile (374 × 88) e
// o desktop (551,5 × 164) no mesmo componente, trocados por media query — não
// precisa de gate de largura aqui.
const vipStyle = computed(() =>
  props.card.vipTiers === true ? vipTierStyle(vipTier.value) : undefined,
);

// Cards informativos ('none'), bloqueados ('coming_soon') e desativados pelo
// contrato não são clicáveis.
const isInteractive = computed(
  () =>
    !isLocked.value &&
    props.card.action !== 'coming_soon' &&
    props.card.action !== 'none',
);

// Card inteiro = 1 imagem exportada do Figma (gradiente + arte + texto + CTA,
// com o bleed embutido como transparência no topo). Mobile e desktop têm artes
// diferentes (layout distinto); o <picture> troca por breakpoint.
const bgUrl = computed(() => loyaltyHubCardArt(props.card));

const desktopUrl = computed(() => loyaltyHubCardArt(props.card, true));

const isEager = computed(() => (props.index ?? 0) < EAGER_CARDS);

// Fade-in da arte: sem isso ela aparece de estalo dentro da caixa reservada.
const imgEl = ref<HTMLImageElement | null>(null);
const isLoaded = ref(false);

// Arte já em cache pode chegar `complete` antes do listener existir — aí o
// evento `load` nunca dispara e o card ficaria preso em `opacity: 0`.
onMounted(() => {
  if (imgEl.value?.complete) isLoaded.value = true;
});

// Últimos 3 cards = linha de 3 colunas no desktop (span menor).
const isLast3 = computed(
  () =>
    props.index !== undefined &&
    props.total !== undefined &&
    props.index >= props.total - 3,
);

// Texto está embutido na imagem — repassa pro alt pra manter acessibilidade.
// Desativado: o ribbon é decorativo (aria-hidden), então o estado tem que
// chegar no leitor de tela por aqui.
// (No card de VIP isto não se aplica: lá o texto é DOM de verdade.)
const alt = computed(() =>
  [
    props.card.title,
    props.card.subtitle,
    isLocked.value ? lockedLabel.value : '',
  ]
    .filter(Boolean)
    .join('. '),
);

function onSelect() {
  if (!isInteractive.value) return;
  emit('select', props.card);
}
</script>

<template>
  <!-- Card composto por tier de VIP: substitui a arte estática inteira, então
       entra no lugar do <button> e recebe as mesmas classes de span do grid (o
       scoped CSS daqui alcança a raiz do filho). -->
  <LoyaltyHubRouletteVipCard
    v-if="vipStyle"
    :class="[`lh-card--${card.layout}`, { 'lh-card--last3': isLast3 }]"
    :cta="card.cta"
    :subtitle="card.subtitle"
    :tier="vipTier as number"
    :title="card.title"
    @select="onSelect"
  />

  <button
    v-else
    class="lh-card"
    :class="[
      `lh-card--${card.layout}`,
      {
        'lh-card--last3': isLast3,
        'lh-card--locked': isLocked,
        'lh-card--inert': !isInteractive,
      },
    ]"
    :disabled="!isInteractive"
    type="button"
    @click="onSelect"
    @dragstart.prevent
  >
    <picture>
      <source
        media="(min-width: 1024px)"
        :srcset="desktopUrl"
      />
      <img
        ref="imgEl"
        :alt="alt"
        class="lh-card__img"
        :class="{ 'is-loaded': isLoaded }"
        decoding="async"
        :loading="isEager ? 'eager' : 'lazy'"
        :src="bgUrl"
        @error="isLoaded = true"
        @load="isLoaded = true"
      />
    </picture>

    <!--
      Ribbon no topo-esquerdo em vez do BaseComingSoonOverlay (cadeado central
      + véu): o overlay cobria a arte e a proposta aqui é o oposto — o card
      continua legível, o cadeado só sinaliza. `aria-hidden` porque o estado já
      chega no leitor de tela pelo `alt` da arte.
      Ancorado na caixa do botão, que começa DEPOIS do bleed (o bleed sobe por
      margin-top negativo) — então `top` conta do primeiro pixel do corpo do
      card, não da ponta da arte.
    -->
    <span
      v-if="isLocked"
      aria-hidden="true"
      class="lh-card__ribbon"
    >
      <NuxtIcon
        filled
        name="mini-games/locked"
      />
      {{ lockedLabel }}
    </span>
  </button>
</template>

<style scoped lang="scss">
// O card É a imagem (tamanho/cantos/bleed já vêm do Figma).
.lh-card {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  // Bleed da arte transborda pra cima sem ser cortado.
  overflow: visible;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  // <picture> transparente ao layout: a <img> se comporta como filha do card.
  picture {
    display: contents;
  }

  // Card sem ação (desativado, 'coming_soon' ou 'none') não intercepta ponteiro:
  // sem cursor de mão e sem clique. O `:disabled` já suprimiria o evento, mas
  // suprimir não é o mesmo que repassar — sem isto a caixa dele continuaria
  // engolindo o toque em vez de deixar passar pro que houver atrás.
  // (O bleed sobreposto ao card anterior é problema de todo card, ativo ou não,
  // e quem resolve é o `pointer-events: none` da .lh-card__img mais abaixo.)
  &.lh-card--inert {
    pointer-events: none;
  }

  &:not(:disabled):active {
    filter: brightness(0.94);
  }
}

.lh-card__img {
  display: block;
  width: 100%;
  height: auto;
  // A arte NUNCA é alvo de clique — quem recebe é a caixa do <button>, que é
  // exatamente o corpo do card. Sem isso, a faixa de bleed (que sobe por
  // margin-top negativo e é maior que o gap: 32px contra 16px no mobile, 48
  // contra 16 no desktop) fica pairando sobre os últimos pixels do card ANTERIOR
  // e rouba o clique dele — era o bug de "clicar na base de um card abre o card
  // seguinte". Como a <img> sai do hit-test, o ponteiro atravessa o bleed e acha
  // a caixa de quem está embaixo: o card certo.
  pointer-events: none;
  -webkit-user-drag: none;
  user-select: none;
  // Escondida até carregar; o fade entra pelo `.is-loaded` (via `animation`, e
  // não `transition`, porque o reset global do main.scss zera transition no app
  // inteiro). O `@error` também liga a classe, pra arte quebrada não sumir.
  opacity: 0;
}

.lh-card__img.is-loaded {
  animation: lh-card-img-in 0.18s ease forwards;
}

@keyframes lh-card-img-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ---------- desativado (enabled: false) ---------- */
// O desfoque vai na IMAGEM, não num overlay com backdrop-filter. Três motivos:
// 1. o bleed da arte vive FORA da caixa do botão (margin-top negativo), então
//    um overlay `inset: 0` cobriria só o corpo e deixaria a ponta da arte
//    nítida por cima de um card borrado;
// 2. `backdrop-filter` aqui custaria caro — o backdrop inclui as camadas de
//    névoa animadas do painel;
// 3. o card É uma imagem só, então borrá-la dá exatamente o mesmo resultado.
// Dosagem: desfoque de 1px só embaça o texto assado na arte, sem apagá-la — o
// card tem que continuar reconhecível, quem comunica o bloqueio é o ribbon.
// O escurecimento é de leve (0,82) e a dessaturação idem: o suficiente pro card
// recuar em relação aos ativos ao lado. A borda fica minimamente esfumaçada
// nesse raio — some contra o fundo escuro do painel.
.lh-card--locked .lh-card__img {
  filter: blur(1px) brightness(0.82) saturate(0.85);
}

// Pílula com cadeado no topo-esquerdo do corpo do card.
.lh-card__ribbon {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  // Chapado em vez de backdrop-blur: o fundo dele é a arte JÁ desfocada, e um
  // segundo blur empilhado sobre ela só cobraria composite sem mudar nada.
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  line-height: 100%;
  white-space: nowrap;
  pointer-events: none;

  :deep(.nuxt-icon) {
    display: inline-flex;
    font-size: 11px;
    // NuxtIcon injeta um <svg> com margin-bottom próprio; zera pra alinhar o
    // cadeado com a linha do texto.
    margin-bottom: 0;
  }

  :deep(svg) {
    margin-bottom: 0;
  }
}

/* ---------- span no grid de 6 colunas ---------- */
// Mobile: full ocupa a linha toda; half = metade (2 por linha).
.lh-card--full {
  grid-column: span 6;
}

.lh-card--half {
  grid-column: span 3;
}

// RESERVA DA CAIXA DA ARTE (`aspect-ratio`): sem isso a <img> mede ZERO de
// altura até o byte chegar — e como o card É a imagem (nenhum texto, nenhuma
// altura mínima no DOM), o painel abria colapsado e ia se esticando conforme
// cada webp caía, empurrando os de baixo. Era o tilt da abertura sem cache.
// `auto <razão>` e não `<razão>` seco: a razão declarada só vale ENQUANTO não
// existe a intrínseca; depois de carregar quem manda é a imagem. Assim um asset
// de proporção diferente vindo do contrato (`background`) não estica.
// Os números são as dimensões reais das artes exportadas do Figma, que são
// uniformes por classe: mobile full 1122x360 e half 537x492.
// Mobile full: imagem 374x120 = corpo (88) + bleed (32) no topo. margin-top
// -32/374 = -8.556% puxa o bleed pra cima sem inflar o gap.
.lh-card--full .lh-card__img {
  aspect-ratio: auto 1122 / 360;
  margin-top: -8.556%;
}

.lh-card--half .lh-card__img {
  aspect-ratio: auto 537 / 492;
}

/* ---------- desktop (2-col + última linha de 3) ---------- */
@media (min-width: 1024px) {
  // Todos viram 2-col (span 3); os 3 últimos, 3-col (span 2).
  .lh-card--full,
  .lh-card--half {
    grid-column: span 3;
  }

  .lh-card--last3 {
    grid-column: span 2;
  }

  // Desktop: imagem 552x212 (2-col) ou 362x212 (3-col), corpo 164 + bleed 48.
  // margin-top = -bleed/largura pro overlap uniforme (escala responsivo).
  // `aspect-ratio`: dimensões reais das artes desktop — 1655x636 nas de 2-col e
  // 1098x636 nas 3-col (mesmo motivo do mobile, ver acima). A do `checkin-diario`
  // é a única fora do padrão (1087 contra 1098), diferença de ~2px na tela que o
  // `auto` acerta no load.
  .lh-card--full .lh-card__img,
  .lh-card--half .lh-card__img {
    aspect-ratio: auto 1655 / 636;
    margin-top: -8.696%; // 48 / 552
  }

  .lh-card--last3 .lh-card__img {
    aspect-ratio: auto 1098 / 636;
    margin-top: -13.26%; // 48 / 362
  }

  // Card desktop é bem maior (corpo 164px contra 88px do mobile): o ribbon
  // acompanha, senão fica miúdo demais na arte.
  .lh-card__ribbon {
    top: 12px;
    left: 12px;
    padding: 4px 10px;
    font-size: 11px;

    :deep(.nuxt-icon) {
      font-size: 13px;
    }
  }
}
</style>
